import useSiteSettings from "../lib/useSiteSettings";
import { usePhoneLayout } from "../lib/PhoneLayout";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { HOME_COLLECTIONS } from "../lib/nav";
import SEO from "../components/SEO";
import { Reveal, SectionHead } from "../components/Reveal";
import WordReveal, { useReducedData } from "../components/WordReveal";
import ProductCard from "../components/ProductCard";
import FeatureStrip from "../components/FeatureStrip";
import { HeritageStrip } from "../components/CompanySections";
import BrandText from "../components/BrandText";

const U = (id, w = 1600) => `/assets/interiors/${id}-${w <= 320 ? 320 : w <= 640 ? 640 : w <= 1000 ? 1000 : 1600}.webp`;
const cut = (slug) => `/assets/products/hero/${slug}.webp`;

function heroItemCount() {
  if (typeof window === "undefined" || !window.matchMedia) return 3;
  return window.matchMedia("(min-width: 1024px)").matches ? 7 : window.matchMedia("(min-width: 640px)").matches ? 5 : 3;
}

function useHeroItemCount() {
  const [count, setCount] = useState(heroItemCount);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const queries = [window.matchMedia("(min-width: 640px)"), window.matchMedia("(min-width: 1024px)")];
    const update = () => setCount(heroItemCount());
    queries.forEach((query) => query.addEventListener ? query.addEventListener("change", update) : query.addListener(update));
    return () => queries.forEach((query) => query.removeEventListener ? query.removeEventListener("change", update) : query.removeListener(update));
  }, []);
  return count;
}

function usePageVisible() {
  const [visible, setVisible] = useState(() => typeof document === "undefined" || !document.hidden);
  useEffect(() => {
    const update = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);
  return visible;
}

const HERO_ROWS = [
  ["aurelia-table-lamp", "terra-vase", "halo-candle-holders", "ceto-glass-vase", "muse-sculpture", "noor-candle-duo", "arc-bookends"],
  ["luna-pendant-light", "onyx-marble-platter", "vela-photo-frame", "alto-carafe-set", "monolith-marble-object", "meridian-serving-tray", "strata-desk-organizer"],
  ["ceto-glass-vase", "arc-bookends", "aurelia-table-lamp", "noor-candle-duo", "terra-vase", "orbit-mirror", "halo-candle-holders"],
  ["sienna-bowl-set", "muse-sculpture", "riva-console-object", "ember-floor-lamp", "alto-carafe-set", "ritual-gift-box", "luna-pendant-light"],
];

const DEFAULT_SECTIONS = [
  { id: "hero", enabled: true, order: 1, eyebrow: "S H & SONS ® presents", title: "Timeless Forms", subtitle: "Crafted in India. For collections around the world.", primary_label: "Explore Products", primary_href: "/shop", secondary_label: "Request Catalogue", secondary_href: "/catalogue" },
  { id: "collections", enabled: true, order: 2, title: "Discover the collections" },
  { id: "bestsellers", enabled: true, order: 3, eyebrow: "Modern. Minimal. Made in India.", title: "Objects of quiet presence", subtitle: "Pieces that sit between crafting and creating — drawn with restraint, finished by hand.", cta_label: "Explore Products", cta_href: "/shop" },
  { id: "story", enabled: true, order: 4, eyebrow: "Who we are", body: "We are S H & SONS ®, a home décor manufacturer and exporter based in Moradabad, India. We work with international importers, wholesalers and retail brands on bulk collections in metal, glass, wood and marble.", subtitle: "S H & SONS ® specialises in" },
  { id: "features", enabled: true, order: 5 },
  { id: "arrivals", enabled: true, order: 6, eyebrow: "Just landed", title: "New Arrivals", subtitle: "Explore new designs for your next wholesale collection.", cta_label: "View all new arrivals", cta_href: "/shop?tag=new" },
  { id: "makers", enabled: true, order: 7, eyebrow: "The makers", title: "Made by S H & SONS ®", body: "From your first product shortlist to the details of a sourcing brief, our team helps you explore materials, finishes, sampling and bulk production requirements. Tell us about your market and the collection you want to create.", cta_label: "hello@shandsons.in", cta_href: "mailto:hello@shandsons.in" },
  { id: "signature", enabled: true, order: 8, eyebrow: "S H & SONS ® presents", title: "The Moradabad Edit", body: "Our definitive metalwork — spun, cast and brushed by the hands that built this town's name.", cta_href: "/collections/decorative-objects" },
];

const heroRows=config=>Array.isArray(config.floating_rows)?config.floating_rows.map(row=>(row.items||[]).filter(item=>item.enabled!==false&&item.image)):HERO_ROWS.map(row=>row.map(slug=>({image:cut(slug),alt:''})));
const heroProductHref = item => {
  const code = item.image?.match(/\b(sh-\d+)(?:-hero)?\.webp(?:\?.*)?$/i)?.[1];
  return code ? `/product/${code.toLowerCase()}` : "/collections/christmas-2027";
};
function HeroRow({ items, rowIndex, visibleCount }) {
  const reduced = useReducedMotion();
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-7 gap-x-4 sm:gap-x-8">
      {items.slice(0, visibleCount).map((item, i) => (
        <motion.div
          key={`${item.image}-${i}`}
          initial={reduced ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduced ? 0 : 0.9, delay: reduced ? 0 : 0.08 * i + rowIndex * 0.12, ease: [0.22, 1, 0.36, 1] }}
          className="h-24 sm:h-32 lg:h-40 flex items-end justify-center"
        >
          <Link to={heroProductHref(item)} aria-label={`View ${item.alt || "Christmas collection"}`} className="flex h-full w-full items-end justify-center rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brass">
            <img src={item.image} alt={item.alt||""} loading={rowIndex > 1 ? "lazy" : "eager"} decoding="async" fetchPriority="low" className="max-h-full max-w-full object-contain cutout-shadow" />
          </Link>
        </motion.div>
      ))}
    </div>
  );
}

function Hero({ config }) {
  const reduced = useReducedMotion();
  const visibleCount = useHeroItemCount();
  return (
    <section className="relative bg-beige -mt-[76px] pt-[108px] pb-40 sm:pb-16 overflow-hidden" data-testid="hero-section">
      <div className="mx-auto max-w-[1500px] px-6 sm:px-12 space-y-10 sm:space-y-14">
        <HeroRow items={heroRows(config)[0]||[]} rowIndex={0} visibleCount={visibleCount} />
        <HeroRow items={heroRows(config)[1]||[]} rowIndex={1} visibleCount={visibleCount} />
        <div className="text-center py-6 sm:py-10">
          <motion.img
            src={config.logo_image||"/assets/shsons-emblem-original.svg"}
            alt="S H & SONS ®"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            width={667}
            height={700}
            initial={reduced ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reduced ? 0 : 0.45, duration: reduced ? 0 : 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="h-[124px] sm:h-[148px] lg:h-[168px] object-contain w-auto max-w-full mx-auto mb-8"
            data-testid="hero-brand-logo"
          />
          <motion.p initial={reduced ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: reduced ? 0 : 0.6, duration: reduced ? 0 : 0.8 }} className="eyebrow mb-5" data-testid="hero-eyebrow">
            <BrandText text={config.eyebrow} />
          </motion.p>
          <motion.h1
            initial={reduced ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reduced ? 0 : 0.7, duration: reduced ? 0 : 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-light uppercase tracking-[0.18em] sm:tracking-[0.22em] text-[9.5vw] sm:text-6xl lg:text-[88px] leading-none text-ink"
            data-testid="hero-title"
          >
            <BrandText text={config.title} />
          </motion.h1>
          <motion.p
            initial={reduced ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reduced ? 0 : 0.9, duration: reduced ? 0 : 0.9 }}
            className="font-script italic text-xl sm:text-2xl lg:text-[30px] text-charcoal/80 mt-5"
            data-testid="hero-subtitle"
          >
            <BrandText text={config.subtitle} />
          </motion.p>
        </div>
        <HeroRow items={heroRows(config)[2]||[]} rowIndex={2} visibleCount={visibleCount} />
        <HeroRow items={heroRows(config)[3]||[]} rowIndex={3} visibleCount={visibleCount} />
      </div>
      <div className="absolute bottom-8 inset-x-0 sm:inset-x-auto sm:right-12 flex flex-wrap justify-center gap-3 px-6">
        <Link to={config.primary_href} data-testid="hero-explore-button" className="pill-light px-6">{config.primary_label}</Link>
        <Link to={config.secondary_href} data-testid="hero-discover-button" className="pill-dark px-6">{config.secondary_label}</Link>
      </div>
    </section>
  );
}

function DiscoverCollections({ config }) {
  const rail = useRef(null);
  const { events } = useRailAutoAdvance(rail, true, HOME_COLLECTIONS.length);
  return (
    <section className="mx-auto max-w-[1300px] px-6 py-24 lg:py-32" data-testid="featured-collections" {...events}>
      <WordReveal text={config.title} className="h-display text-center text-4xl sm:text-5xl lg:text-[64px]" />
      <div ref={rail} className="mt-16 grid grid-flow-col auto-cols-[200px] lg:grid-flow-row lg:auto-cols-auto lg:grid-cols-6 gap-6 lg:gap-2 xl:gap-4 overflow-x-auto lg:overflow-visible no-scrollbar snap-x snap-proximity pb-3" aria-label="Browse collections">
        {HOME_COLLECTIONS.map((c, i) => (
          <Reveal key={c.slug} delay={i * 0.06}>
            <Link to={`/collections/${c.slug}`} data-testid={`collection-card-${c.slug}`} className="group min-w-0 flex flex-col items-center text-center snap-start">
              <div className="w-full max-w-24 sm:max-w-32 aspect-square flex items-center justify-center">
                <img src={c.sketch} alt={`${c.name} sketch`} loading="lazy" decoding="async" className="max-w-full max-h-full object-contain transition-transform duration-700 group-hover:scale-110 group-hover:-rotate-3 motion-reduce:transition-none" />
              </div>
              <p className="mt-4 text-[13px] lg:text-[clamp(11px,1.1vw,13px)] whitespace-nowrap tracking-wide text-charcoal group-hover:text-brass transition-colors duration-200">{c.name}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function railStep(rail) {
  const card = rail.firstElementChild;
  return card ? card.getBoundingClientRect().width + (parseFloat(getComputedStyle(rail).columnGap) || 0) : rail.clientWidth;
}

function useRailAutoAdvance(ref, autoAdvance, itemCount) {
  const reduced = useReducedMotion();
  const reducedData = useReducedData();
  const phone = usePhoneLayout();
  const pause = useRef({ hover: false, focus: false, pointer: false, inView: false, until: 0 });
  const scheduleAdvance = useRef(() => {});
  const defer = () => { pause.current.until = Date.now() + 12000; scheduleAdvance.current(); };
  useEffect(() => {
    const rail = ref.current;
    if (phone || !rail || !autoAdvance || reduced !== false || reducedData || itemCount < 2) return undefined;
    let timer;
    const schedule = () => {
      window.clearTimeout(timer);
      const state = pause.current;
      if (document.hidden || !state.inView || state.hover || state.focus || state.pointer || rail.scrollWidth - rail.clientWidth <= 1) return;
      timer = window.setTimeout(() => {
        if (document.hidden || !state.inView || state.hover || state.focus || state.pointer) return;
        const maximum = rail.scrollWidth - rail.clientWidth;
        if (maximum > 1) rail.scrollTo({ left: rail.scrollLeft >= maximum - 2 ? 0 : Math.min(maximum, rail.scrollLeft + railStep(rail)), behavior: "smooth" });
        state.until = Date.now() + 5000;
        schedule();
      }, Math.max(1000, state.until - Date.now()));
    };
    scheduleAdvance.current = schedule;
    const observer = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(([entry]) => {
      pause.current.inView = entry.isIntersecting && entry.intersectionRatio >= 0.35;
      if (pause.current.inView) pause.current.until = Date.now() + 5000;
      schedule();
    }, { threshold: [0, 0.35] });
    if (observer) observer.observe(rail);
    else { pause.current.inView = true; pause.current.until = Date.now() + 5000; schedule(); }
    const resized = () => { pause.current.until = Date.now() + 5000; schedule(); };
    const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(resized);
    if (resizeObserver) resizeObserver.observe(rail);
    else window.addEventListener("resize", resized);
    const releasePointer = () => {
      if (pause.current.pointer) { pause.current.pointer = false; pause.current.until = Date.now() + 12000; schedule(); }
    };
    const visibilityChanged = () => { pause.current.until = Date.now() + 5000; schedule(); };
    window.addEventListener("pointerup", releasePointer);
    window.addEventListener("pointercancel", releasePointer);
    document.addEventListener("visibilitychange", visibilityChanged);
    return () => {
      window.clearTimeout(timer);
      scheduleAdvance.current = () => {};
      observer?.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener("resize", resized);
      window.removeEventListener("pointerup", releasePointer);
      window.removeEventListener("pointercancel", releasePointer);
      document.removeEventListener("visibilitychange", visibilityChanged);
    };
  }, [phone, autoAdvance, reduced, reducedData, itemCount, ref]);
  return {
    defer,
    events: {
      onPointerEnter: (event) => { if (event.pointerType === "mouse" || event.pointerType === "pen") { pause.current.hover = true; scheduleAdvance.current(); } },
      onPointerLeave: () => { pause.current.hover = false; defer(); },
      onPointerDown: () => { pause.current.pointer = true; defer(); },
      onWheelCapture: defer,
      onKeyDownCapture: defer,
      onFocusCapture: () => { pause.current.focus = true; scheduleAdvance.current(); },
      onBlurCapture: (event) => { if (!event.currentTarget.contains(event.relatedTarget)) { pause.current.focus = false; defer(); } },
    },
  };
}

function ProductRail({ eyebrow, title, sub, products, variant = "lifestyle", testId, cta, autoAdvance = false }) {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const phone = usePhoneLayout();
  const { defer, events } = useRailAutoAdvance(ref, autoAdvance, products.length);
  const scrollBy = (dir) => {
    defer();
    const rail = ref.current;
    rail?.scrollBy({ left: dir * railStep(rail), behavior: reduced ? "auto" : "smooth" });
  };
  return (
    <section className="py-16 lg:py-24" data-testid={testId} {...events}>
      <div className="mx-auto max-w-[1300px] px-6">
        <SectionHead eyebrow={eyebrow} title={title} sub={sub} />
      </div>
      <div className={phone ? "relative mt-8" : "relative mt-14"}>
        <div ref={ref} tabIndex={0} role="region" aria-label={title + " products"} aria-roledescription={autoAdvance ? "carousel" : undefined}
          onKeyDown={(event) => { if (event.target === event.currentTarget && ["ArrowLeft", "ArrowRight"].includes(event.key)) { event.preventDefault(); scrollBy(event.key === "ArrowLeft" ? -1 : 1); } }}
          className="product-rail flex gap-5 sm:gap-7 overflow-x-auto no-scrollbar snap-x snap-mandatory px-6 sm:px-12 pb-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brass">
          {(phone ? products.slice(0, 6) : products).map((p, i) => (
            <ProductCard compact={phone} key={p.slug} product={p} index={i} variant={variant} className="w-[68vw] sm:w-[300px] lg:w-[320px] shrink-0 snap-start" />
          ))}
        </div>
        <div className="hidden lg:flex absolute -top-24 right-12 gap-2">
          <button onClick={() => scrollBy(-1)} aria-label="Scroll left" data-testid={`${testId}-prev`} className="w-11 h-11 rounded-full bg-ink text-white flex items-center justify-center hover:bg-charcoal transition-colors duration-200">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <button onClick={() => scrollBy(1)} aria-label="Scroll right" data-testid={`${testId}-next`} className="w-11 h-11 rounded-full bg-ink text-white flex items-center justify-center hover:bg-charcoal transition-colors duration-200">
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
      {cta && (
        <div className="mt-10 text-center">
          <Link to={cta.href} data-testid={cta.testId} className="pill-dark px-8">{cta.label}</Link>
        </div>
      )}
    </section>
  );
}

function WhoWeAre({ collections, config }) {
  const specialities = collections.filter((c) => ["candleholders-lanterns", "decorative-objects", "trays-serveware", "wood-marble"].includes(c.slug));
  return (
    <section className="mx-auto max-w-[1000px] px-6 py-20 lg:py-28 text-center" data-testid="brand-story-section">
      <p className="eyebrow mb-6"><BrandText text={config.eyebrow} /></p>
      <WordReveal
        as="p"
        text={config.body}
        className="h-display text-2xl sm:text-4xl lg:text-[44px] leading-[1.25] text-balance"
      />
      <p className="mt-14 text-[13px] text-mocha"><BrandText text={config.subtitle} /></p>
      <p className="mt-3 h-display text-2xl sm:text-4xl lg:text-[44px] flex flex-wrap justify-center gap-x-3" data-testid="specialities">
        {specialities.map((c, i) => (
          <Link key={c.slug} to={`/collections/${c.slug}`} data-testid={`speciality-${c.slug}`} className="hover:text-brass transition-colors duration-200">
            {c.name}
            <sup className="text-[0.4em] ml-1 text-mocha">{c.product_count}</sup>
            {i < specialities.length - 1 ? "," : ""}
          </Link>
        ))}
      </p>
    </section>
  );
}

const TILES = [
  ["MORADABAD", "font-semibold tracking-[0.2em] text-[14px]"],
  ["S H & SONS ®", "font-light text-[26px]"],
  ["Metal", "font-script italic text-[30px]"],
  ["EXPORT GRADE", "font-medium tracking-[0.3em] text-[12px]"],
  ["Glass", "font-script italic text-[30px]"],
  ["HANDCRAFTED", "font-semibold tracking-[0.25em] text-[13px]"],
  ["Wood", "font-script italic text-[30px]"],
  ["1,500+ ARTISANS", "font-light tracking-[0.2em] text-[14px]"],
  ["Marble", "font-script italic text-[30px]"],
  ["MADE IN INDIA", "font-medium tracking-[0.3em] text-[12px]"],
  ["SINCE 1986", "font-light text-[22px]"],
  ["DESIGN FIRST", "font-semibold tracking-[0.2em] text-[13px]"],
];

function TileRow({ reverse = false, running, tiles }) {
  const source=Array.isArray(tiles)?tiles.map(t=>[t.text,"font-medium text-lg"]):TILES;
  const row = [...source, ...source];
  return (
    <div className={`flex w-max gap-5 ${reverse ? "animate-marquee-reverse" : "animate-marquee"}`} style={{ animationPlayState: running ? "running" : "paused" }}>
      {row.map(([t, cls], i) => (
        <div key={`${t}-${i}`} className="w-[260px] h-[130px] rounded-2xl bg-[#232323] flex items-center justify-center text-white/90">
          <span className={cls}><BrandText text={t} /></span>
        </div>
      ))}
    </div>
  );
}

function Makers({ config }) {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "100px 0px" });
  const reduced = useReducedMotion();
  const reducedData = useReducedData();
  const pageVisible = usePageVisible();
  const running = inView && pageVisible && !reduced && !reducedData;
  return (
    <section ref={ref} id="craftsmanship" className="bg-[#161616] text-white overflow-hidden" data-testid="manufacturing-section">
      <div className="mx-auto max-w-3xl px-6 pt-24 pb-16 text-center">
        <p className="eyebrow text-white/50 mb-5"><BrandText text={config.eyebrow} /></p>
        <WordReveal dark text={config.title} className="h-display text-4xl sm:text-5xl lg:text-[64px]" />
        <p className="mt-6 text-[13px] sm:text-[14px] text-white/70 leading-relaxed max-w-xl mx-auto">
          <BrandText text={config.body} />
        </p>
        <a href={config.cta_href} data-testid="manufacturing-cta" className="mt-6 inline-block text-[12px] underline underline-offset-4 text-white/80 hover:text-white">{config.cta_label}</a>
      </div>
      <div className="relative h-[360px] sm:h-[420px]">
        <div className="absolute inset-x-[-20%] top-1/2 -translate-y-1/2 -rotate-6 space-y-5">
          <TileRow tiles={config.tiles} running={running} />
          <TileRow tiles={config.tiles} running={running} reverse />
          <TileRow tiles={config.tiles} running={running} />
        </div>
      </div>
    </section>
  );
}

function Presents({ config }) {
  const imgs = config.images??["1616486338812-3dadae4b4ace", "1600210492486-724fe5c67fb0", "1618220179428-22790b461013"].map(id=>({image:U(id,1000),alt:"Signature collection"}));
  return (
    <section className="mx-auto max-w-[1400px] px-6 py-24 lg:py-32 text-center" data-testid="signature-collection-section">
      <p className="eyebrow mb-4"><BrandText text={config.eyebrow} /></p>
      <WordReveal text={config.title} className="h-display text-4xl sm:text-5xl lg:text-[64px]" />
      <Reveal className="mt-12">
        <Link to={config.cta_href} data-testid="signature-explore-button" className="group grid grid-cols-3 rounded-[28px] overflow-hidden">
          {imgs.map((id, i) => (
            <div key={i} className="aspect-[3/4] sm:aspect-[4/5] overflow-hidden">
              <img src={id.image} alt={id.alt||""} loading="lazy" decoding="async" fetchPriority="low" className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105 motion-reduce:transition-none" />
            </div>
          ))}
        </Link>
      </Reveal>
      <p className="mt-8 text-[13px] text-mocha max-w-md mx-auto"><BrandText text={config.body} /></p>
    </section>
  );
}

function PhoneHero({ config }) {
  const reduced = useReducedMotion();
  let remaining = Math.max(0, Math.min(12, Number(config.mobile_image_count ?? 12)));
  const rows = heroRows(config).slice(0, 4).map(row => {
    const items = row.slice(0, Math.min(3, remaining));
    remaining -= items.length;
    return items;
  });
  const floatingRow = (items, row) => items.length > 0 && <div className="phone-floating-products" key={row}>
    {items.map((item, i) => <motion.div key={item.image+i} initial={false}
      animate={reduced ? undefined : { y: [0, (i + row) % 2 ? -3 : 3, 0] }}
      transition={{duration:4.5,delay:(i+row)*.12,repeat:2,ease:"easeInOut"}}>
      <Link to={heroProductHref(item)} aria-label={`View ${item.alt || "Christmas collection"}`} className="flex min-h-11 min-w-11 items-center justify-center rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brass">
        <img src={item.image} alt={item.alt||""} width="64" height="64" decoding="async" />
      </Link>
    </motion.div>)}
  </div>;
  return <section className="home-hero" data-testid="hero-section">
    <div className="home-hero-inner">
      {rows.slice(0,2).map(floatingRow)}
      <img src={config.logo_image||"/assets/shsons-emblem-original.svg"} alt="S H & SONS ®" width="667" height="700" className="home-hero-logo phone-logo-reveal" fetchPriority="high" data-testid="hero-brand-logo" data-phone-brand-mark />
      <p className="eyebrow" data-testid="hero-eyebrow"><BrandText text={config.eyebrow} /></p>
      <h1 data-testid="hero-title"><BrandText text={config.title} /></h1>
      <p className="home-hero-subtitle" data-testid="hero-subtitle"><BrandText text={config.subtitle} /></p>
      <div className="phone-floating-lower">{rows.slice(2).map((items,i)=>floatingRow(items,i+2))}</div>
      <div className="home-hero-actions">
        <Link to={config.primary_href} className="pill-dark" data-testid="hero-explore-button">{config.primary_label}</Link>
        <Link to={config.secondary_href} className="pill-outline" data-testid="hero-discover-button">{config.secondary_label}</Link>
      </div>
    </div>
  </section>;
}

function PhoneDiscoverCollections({ config }) {
  return <section className="home-collections" data-testid="featured-collections">
    <WordReveal text={config.title} className="h-display text-center text-3xl sm:text-4xl lg:text-5xl" />
    <div className="home-collection-grid" aria-label="Browse collections">
      {HOME_COLLECTIONS.map(c => <Link key={c.slug} to={`/collections/${c.slug}`} data-testid={`collection-card-${c.slug}`} className="home-collection-link">
        <img src={c.sketch} alt="" width="112" height="112" loading="lazy" decoding="async" />
        <span>{c.name}</span>
      </Link>)}
    </div>
  </section>;
}

function PhoneWhoWeAre({ collections, config }) {
  const specialities = collections.filter((c) => ["candleholders-lanterns", "decorative-objects", "trays-serveware", "wood-marble"].includes(c.slug));
  return (
    <section className="mx-auto max-w-[1000px] px-6 py-20 lg:py-28 text-center" data-testid="brand-story-section">
      <p className="eyebrow mb-6"><BrandText text={config.eyebrow} /></p>
      <WordReveal
        as="p"
        text={config.body}
        className="font-light text-lg sm:text-2xl lg:text-3xl leading-relaxed text-balance"
      />
      <p className="mt-8 text-sm text-mocha"><BrandText text={config.subtitle} /></p>
      <p className="mt-4 text-sm sm:text-base flex flex-wrap justify-center gap-3" data-testid="specialities">
        {specialities.map((c, i) => (
          <Link key={c.slug} to={`/collections/${c.slug}`} data-testid={`speciality-${c.slug}`} className="px-4 py-3 rounded-full border border-line hover:text-brass transition-colors duration-200">
            {c.name}
            <sup className="text-[0.4em] ml-1 text-mocha">{c.product_count}</sup>
            
          </Link>
        ))}
      </p>
    </section>
  );
}

function PhoneMakers({ config }) {
  return <section id="craftsmanship" className="bg-ink text-white px-6 py-16 sm:py-20 text-center" data-testid="manufacturing-section">
    <div className="max-w-3xl mx-auto">
      <p className="eyebrow text-white/70 mb-5"><BrandText text={config.eyebrow} /></p>
      <WordReveal dark text={config.title} className="h-display text-3xl sm:text-5xl" />
      <p className="mt-6 text-base text-white/80 leading-relaxed max-w-xl mx-auto"><BrandText text={config.body} /></p>
      <div className="mt-8 flex flex-wrap justify-center gap-3" aria-label="Core materials">{["Metal","Wood","Glass","Marble"].map(material=><span key={material} className="border border-white/25 rounded-full px-5 py-2 text-sm">{material}</span>)}</div>
      <a href={config.cta_href} data-testid="manufacturing-cta" className="inline-flex items-center justify-center min-h-11 mt-6 underline underline-offset-4 text-white">{config.cta_label}</a>
    </div>
  </section>;
}


export default function Home() {
  const settings=useSiteSettings();
  const phone = usePhoneLayout();
  const HeroView = phone ? PhoneHero : Hero;
  const DiscoverCollectionsView = phone ? PhoneDiscoverCollections : DiscoverCollections;
  const WhoWeAreView = phone ? PhoneWhoWeAre : WhoWeAre;
  const MakersView = phone ? PhoneMakers : Makers;

  const [bestsellers, setBestsellers] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [collections, setCollections] = useState([]);
  const [sections, setSections] = useState(DEFAULT_SECTIONS);

  useEffect(() => {
    api.products({ tag: "featured", collection: "christmas-2027" }).then(setBestsellers).catch(() => {});
    api.products({ collection: "christmas-2027", tag: "featured" }).then(setNewArrivals).catch(() => {});
    api.collections().then(setCollections).catch(() => {});
    api.site("homepage").then((data) => data.sections && setSections(data.sections)).catch(() => {});
  }, []);

  const renderSection = (section) => {
    if (section.enabled === false) return null;
    if (section.id === "hero") return <HeroView key={section.id} config={section} />;
    if (section.id === "collections") return <DiscoverCollectionsView key={section.id} config={section} />;
    if (section.id === "bestsellers") return <ProductRail key={section.id} autoAdvance testId="bestsellers-section" eyebrow={section.eyebrow} title={section.title} sub={section.subtitle} products={bestsellers} cta={{ label: section.cta_label, href: section.cta_href, testId: "bestsellers-view-all" }} />;
    if (section.id === "story") return <WhoWeAreView key={section.id} collections={collections} config={section} />;
    if (section.id === "features") return <FeatureStrip key={section.id} />;
    if (section.id === "arrivals") return <ProductRail key={section.id} testId="new-arrivals-section" eyebrow={section.eyebrow} title={section.title} sub={section.subtitle} products={newArrivals} variant="cutout" cta={{ label: section.cta_label, href: section.cta_href, testId: "new-arrivals-view-all" }} />;
    if (section.id === "makers") return <MakersView key={section.id} config={section} />;
    if (section.id === "signature") return <Presents key={section.id} config={section} />;
    return null;
  };

  return (
    <div data-testid="home-page">
      <SEO
        title="S H & SONS ® — Timeless Forms. Home Décor Manufacturer & Exporter"
        description="Home décor in metal, glass, wood and marble for international importers, wholesalers and retail brands. Explore products and send a bulk enquiry to S H & SONS ®, Moradabad."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "S H & SONS ®",
          url: window.location.origin,
          logo: `${window.location.origin}/assets/shsons-emblem-original.svg`,
          
          contactPoint: { "@type": "ContactPoint", email: "hello@shandsons.in", telephone: "+91-9870651826" },
        }}
      />
      {[...sections].sort((a, b) => a.order - b.order).map(renderSection)}
      <HeritageStrip />
      {settings.fair_teaser?.enabled!==false&&<section className="mx-auto max-w-[1300px] px-6 py-24 text-center"><p className="eyebrow mb-5">{settings.fair_teaser?.eyebrow||"Meet us"}</p><WordReveal text={settings.fair_teaser?.title||"Trade fairs"} className="h-display text-4xl sm:text-6xl" /><p className="mt-6 text-sm text-mocha">{settings.fair_teaser?.description}</p><Link to={settings.fair_teaser?.href||"/trade-fairs"} className="pill-dark mt-8">{settings.fair_teaser?.button_label||"Request a Meeting"}</Link></section>}
    </div>
  );
}

